import React, { useRef, useState } from 'react';

interface FileUploadProps {
  name: string;
  label: string;
  /** Optional helper line shown under/beside the label. */
  description?: string;
  /** Optional icon shown to the left of the label link. */
  icon?: string;
  /** Hover tip on an eye icon next to the label. */
  tip?: string;
  accept?: string;
  required?: boolean;
  maxSizeMB?: number;
  /** Status text while uploading (e.g. "Uploading..."). */
  status?: string;
  /** Human-readable file name for display (not the storage URL). */
  fileName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileSelect?: (file: File) => void;
  validationError?: string;
  /** Green underlined prompt shown next to the label (e.g. “Upload your pitchdeck”). */
  actionLabel?: string;
}

/** Extract a display-friendly name from a storage URL or plain file name. */
export function getFileDisplayName(value?: string): string {
  if (!value) return 'No file selected';
  if (!value.startsWith('http')) return value;

  try {
    const pathname = decodeURIComponent(new URL(value).pathname);
    const segment = pathname.split('/').pop();
    return segment || 'Uploaded file';
  } catch {
    return 'Uploaded file';
  }
}

export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  label,
  description,
  icon,
  tip,
  accept = 'application/pdf',
  required = false,
  maxSizeMB = 5,
  status,
  fileName: fileNameProp,
  onChange,
  onFileSelect,
  validationError,
  actionLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localFileName, setLocalFileName] = useState<string>('No file selected');
  const [error, setError] = useState<string>('');

  const displayName = status ?? fileNameProp ?? localFileName;

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      // Check file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
      if (file.size > maxSizeBytes) {
        setError(`File size exceeds ${maxSizeMB}MB`);
        setLocalFileName('No file selected');
        e.target.value = ''; // Clear the input
        if (onFileSelect) {
          onFileSelect(null as any);
        }
        return;
      }

      setError('');
      setLocalFileName(file.name);

      if (onFileSelect) {
        onFileSelect(file);
      }
    } else {
      if (onFileSelect) {
        onFileSelect(null as any);
      }
    }

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div>
      <div className="mb-1.5 sm:mb-2 text-left">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {icon ? (
            <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center -ml-3 sm:-ml-10 md:-ml-12 flex-shrink-0">
              <img src={icon} alt="" className="w-full h-full object-contain" />
            </div>
          ) : null}
          {/* Same action as the file field — opens the picker */}
          <button
            type="button"
            onClick={handleButtonClick}
            className="text-sm sm:text-base font-medium text-[#00b0f0] underline underline-offset-2 hover:text-[#33c0f3] focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-sm"
          >
            {status ? 'Uploading…' : label}
          </button>
          {description ? (
            <span className="text-sm sm:text-base font-normal text-white">{description}</span>
          ) : null}
          {tip ? (
            <span className="relative group inline-flex items-center" title={tip}>
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label={tip}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z"
                  />
                  <circle cx="12" cy="12" r="2.25" />
                </svg>
              </button>
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-56 -translate-x-1/2 rounded-md bg-black px-3 py-2 text-left text-xs font-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              >
                {tip}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        id={name}
        name={name}
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Visible input-like button — same picker as the label link */}
      <div className="flex items-center gap-3">
        {actionLabel ? (
          <button
            type="button"
            onClick={handleButtonClick}
            className="flex-shrink-0 whitespace-nowrap text-sm font-medium text-emerald-400 underline underline-offset-2 hover:text-emerald-300"
          >
            {status ? 'Uploading…' : actionLabel}
          </button>
        ) : null}
        <input
          type="text"
          readOnly
          value={displayName}
          onClick={handleButtonClick}
          className={`w-full min-w-0 flex-1 p-2 sm:px-3 sm:py-2 text-sm sm:text-base border rounded sm:rounded-md bg-white text-gray-900 cursor-pointer ${
            validationError ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Click to upload a file"
        />
      </div>

      {/* Error messages */}
      {error && <p className="text-red-500 text-sm mt-1 text-right">{error}</p>}
      {validationError && <p className="text-red-500 text-sm mt-1 text-right">{validationError}</p>}
    </div>
  );
};

export default FileUpload;
